export type Task<T> = (signal: AbortSignal) => Promise<T> | T;

export interface TaskExecutionOptions {
    stagger?: number;
    // 'all': (default)
    waitStrategy?: 'all' | 'last' | 'none';
}

export class ParallelTask<T> {
    private tasks: Task<T>[];
    private controller: AbortController;

    constructor(tasks: Task<T>[]) {
        this.tasks = tasks;
        this.controller = new AbortController();
    }

    public cancel(reason?: string): void {
        this.controller.abort(reason || "parallel task cancellation");
    }

    public async execute(options: TaskExecutionOptions = {}): Promise<any> {
        const { stagger = 0, waitStrategy = 'all' } = options;
        const { signal } = this.controller;
        const promises: Promise<T>[] = [];

        for (let i = 0; i < this.tasks.length; i++) {
            if (signal.aborted) break;

            const taskPromise = Promise.resolve(this.tasks[i](signal));
            promises.push(taskPromise);

            if (stagger > 0 && i < this.tasks.length - 1) {
                await new Promise(resolve => setTimeout(resolve, stagger));
            }
        }

        if (waitStrategy === 'none') return Promise.resolve();
        if (waitStrategy === 'last') return promises[promises.length - 1];

        return Promise.all(promises);
    }
}
